/**
 * ActivityTracker - Kullanıcı Davranış Analitik Sistemi
 * Faz 1 & 2: Session Yönetimi + Event Toplama
 * 
 * Özellikler:
 * - Otomatik session başlatma/bitirme
 * - 30 saniyelik heartbeat
 * - Event batching (5 saniyede bir)
 * - Aktif/Pasif süre takibi
 * - Tab visibility tracking
 * - Device/Browser bilgisi
 * 
 * Kullanım:
 * <ActivityTracker />  // App.js'te AuthProvider içine ekle
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_API = `${BACKEND_URL}/api/analytics`;

// Konfigürasyon - 🚀 OPTIMIZED for Performance
const CONFIG = {
  HEARTBEAT_INTERVAL: 60000,      // 60 saniye (eskisi 30 sn) - Daha az request
  EVENT_BATCH_INTERVAL: 10000,    // 10 saniye (eskisi 5 sn) - Daha büyük batch'ler
  IDLE_THRESHOLD: 120000,         // 120 saniye - idle sayılma süresi
  MAX_EVENTS_PER_BATCH: 100,      // Maksimum batch boyutu artırıldı
  DEBUG: true                      // Console logları AÇIK
};

// Device bilgisi toplama
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  // Device type
  let deviceType = 'desktop';
  if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
  else if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';
  
  // Browser
  let browser = 'unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  
  // OS
  let os = 'unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'MacOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return {
    user_agent: ua,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    device_type: deviceType,
    browser: browser,
    os: os,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    connection_type: navigator.connection?.effectiveType || 'unknown'
  };
};

// UTM parametrelerini al
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null
  };
};

// Referrer bilgisi
const getReferrerInfo = () => {
  const referrer = document.referrer;
  if (!referrer) return { referrer_url: null, referrer_domain: null };
  
  try {
    const url = new URL(referrer);
    return {
      referrer_url: referrer,
      referrer_domain: url.hostname
    };
  } catch {
    return { referrer_url: referrer, referrer_domain: null };
  }
};

// Sayfa tipi belirleme
const getPageType = (pathname) => {
  if (pathname === '/panel/anasayfa' || pathname === '/panel') return 'home';
  if (pathname.includes('/urunler')) return 'product_list';
  if (pathname.includes('/urun/')) return 'product_detail';
  if (pathname.includes('/sepetim')) return 'cart';
  if (pathname.includes('/siparis')) return 'checkout';
  if (pathname.includes('/siparislerim')) return 'orders';
  if (pathname.includes('/cari')) return 'account';
  if (pathname.includes('/wms')) return 'wms';
  if (pathname.includes('/yonetici')) return 'admin';
  return 'other';
};

export const ActivityTracker = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  
  // Refs
  const sessionIdRef = useRef(null);
  const eventQueueRef = useRef([]);
  const heartbeatIntervalRef = useRef(null);
  const batchIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isActiveRef = useRef(true);
  const activeDurationRef = useRef(0);
  const idleDurationRef = useRef(0);
  const lastHeartbeatRef = useRef(Date.now());
  const previousPageRef = useRef(null);
  const pageEnterTimeRef = useRef(Date.now());
  
  // 🆕 Faz 5: İstihbarat için context refs
  const lastProductViewRef = useRef(null);      // Son görüntülenen ürün
  const lastSearchRef = useRef(null);           // Son arama
  const lastCopyRef = useRef(null);             // Son kopyalanan içerik
  const tabHiddenTimeRef = useRef(null);        // Tab gizlenme zamanı
  
  // Debug log
  const log = useCallback((...args) => {
    if (CONFIG.DEBUG) console.log('[ActivityTracker]', ...args);
  }, []);
  
  // API çağrısı helper
  const apiCall = useCallback(async (endpoint, data) => {
    try {
      const response = await fetch(`${ANALYTICS_API}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (err) {
      log('API Error:', endpoint, err.message);
      return { success: false, error: err.message };
    }
  }, [token, log]);
  
  // ==================== SESSION YÖNETİMİ ====================
  
  // Session başlat
  const startSession = useCallback(async () => {
    if (sessionIdRef.current) {
      log('Session zaten var:', sessionIdRef.current);
      return;
    }
    
    const deviceInfo = getDeviceInfo();
    const utmParams = getUtmParams();
    const referrerInfo = getReferrerInfo();
    
    const sessionData = {
      user_type: user?.role === 'client' || user?.role === 'musteri' ? 'customer' : 'staff',
      customer_id: user?.cari_kodu || user?.panel_kodu || user?.id,
      customer_name: user?.cari_unvan || user?.name || user?.email,
      device: deviceInfo,
      ...referrerInfo,
      ...utmParams
    };
    
    const result = await apiCall('/session/start', sessionData);
    
    if (result.success) {
      sessionIdRef.current = result.session_id;
      localStorage.setItem('analytics_session_id', result.session_id);
      log('Session başlatıldı:', result.session_id, '| Returning:', result.is_returning);
      
      // İlk page_view event'i
      queueEvent('page_view', {
        page_url: location.pathname,
        page_type: getPageType(location.pathname),
        is_entry: true
      });
    } else {
      log('Session başlatılamadı:', result.error);
    }
  }, [user, apiCall, location.pathname, log]);
  
  // ==================== HEARTBEAT ====================
  
  const sendHeartbeat = useCallback(async () => {
    if (!sessionIdRef.current) return;
    
    const now = Date.now();
    const timeSinceLastHB = now - lastHeartbeatRef.current;
    
    // Aktif/Pasif süre hesapla
    const currentStatus = isActiveRef.current ? 'active' : 'idle';
    
    const heartbeatData = {
      session_id: sessionIdRef.current,
      status: currentStatus,
      current_page: location.pathname,
      current_page_type: getPageType(location.pathname),
      active_duration_delta: isActiveRef.current ? timeSinceLastHB : 0,
      idle_duration_delta: !isActiveRef.current ? timeSinceLastHB : 0
    };
    
    // Sepet bilgisi varsa ekle (localStorage'dan)
    const cartData = localStorage.getItem('sepet_ozet');
    if (cartData) {
      try {
        const cart = JSON.parse(cartData);
        heartbeatData.cart_value = cart.total || 0;
        heartbeatData.cart_items = cart.count || 0;
      } catch {}
    }
    
    await apiCall('/session/heartbeat', heartbeatData);
    
    lastHeartbeatRef.current = now;
    log('Heartbeat gönderildi | Status:', currentStatus);
  }, [apiCall, location.pathname, log]);
  
  // ==================== EVENT BATCHING ====================
  
  // Eventleri toplu gönder
  const flushEvents = useCallback(async () => {
    if (!sessionIdRef.current || eventQueueRef.current.length === 0) return;
    
    const eventsToSend = [...eventQueueRef.current];
    eventQueueRef.current = [];
    
    const result = await apiCall('/events/batch', {
      session_id: sessionIdRef.current,
      events: eventsToSend
    });
    
    if (result.success) {
      log('Events gönderildi:', result.logged);
    } else {
      // Başarısız olursa kuyruğa geri ekle
      eventQueueRef.current = [...eventsToSend, ...eventQueueRef.current];
      log('Event gönderimi başarısız, kuyruğa geri eklendi');
    }
  }, [apiCall, log]);
  
  // Event kuyruğuna ekle
  const queueEvent = useCallback((eventType, data = {}) => {
    if (!sessionIdRef.current) return;
    
    const event = {
      event_type: eventType,
      timestamp: new Date().toISOString(),
      page_url: location.pathname,
      page_type: getPageType(location.pathname),
      customer_id: user?.cari_kodu || user?.panel_kodu || user?.id || null,
      customer_name: user?.cari_unvan || user?.name || user?.email || null,
      data
    };
    
    eventQueueRef.current.push(event);
    log('Event kuyruğa eklendi:', eventType, '| Kuyruk:', eventQueueRef.current.length);
    
    // Kuyruk doluysa hemen gönder
    if (eventQueueRef.current.length >= CONFIG.MAX_EVENTS_PER_BATCH) {
      flushEvents();
    }
  }, [location.pathname, log, flushEvents, user]);
  
  // Session bitir
  const endSession = useCallback(async (reason = 'normal') => {
    if (!sessionIdRef.current) return;
    
    // Kalan eventleri gönder
    await flushEvents();
    
    // Session'ı bitir
    await apiCall('/session/end', {
      session_id: sessionIdRef.current,
      reason
    });
    
    log('Session sonlandırıldı:', sessionIdRef.current, '| Reason:', reason);
    
    // localStorage temizle
    sessionIdRef.current = null;
    localStorage.removeItem('analytics_session_id');
    localStorage.removeItem('analytics_intel_product');
    localStorage.removeItem('analytics_intel_search');
    localStorage.removeItem('analytics_intel_copy');
  }, [apiCall, flushEvents, log]);
  
  // ==================== AKTİVİTE TAKİBİ ====================
  
  // Aktivite algılama
  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (!isActiveRef.current) {
      isActiveRef.current = true;
      log('Kullanıcı aktif');
    }
  }, [log]);
  
  // Idle kontrolü
  const checkIdle = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    
    if (timeSinceActivity > CONFIG.IDLE_THRESHOLD && isActiveRef.current) {
      isActiveRef.current = false;
      log('Kullanıcı idle oldu');
    }
  }, [log]);
  
  // ==================== SAYFA DEĞİŞİKLİĞİ ====================
  
  useEffect(() => {
    if (!sessionIdRef.current) return;
    
    const now = Date.now();
    
    // Önceki sayfanın süresini hesapla
    if (previousPageRef.current) {
      const duration = now - pageEnterTimeRef.current;
      
      queueEvent('page_exit', {
        page_url: previousPageRef.current,
        page_type: getPageType(previousPageRef.current),
        duration_ms: duration
      });
    }
    
    // Yeni sayfa girişi
    queueEvent('page_view', {
      page_url: location.pathname,
      page_type: getPageType(location.pathname),
      referrer_page: previousPageRef.current
    });
    
    previousPageRef.current = location.pathname;
    pageEnterTimeRef.current = now;
    
  }, [location.pathname, queueEvent]);
  
  // ==================== TAB VİSİBİLİTY (FAZ 5: İSTİHBARAT) ====================
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();
      
      if (document.hidden) {
        // Tab gizlendi - Rakibe mi gidiyor?
        tabHiddenTimeRef.current = now;
        
        // 🆕 Faz 5: İstihbarat verisi localStorage'dan oku
        let lastProduct = null;
        let lastSearch = null;
        let lastCopy = null;
        
        try {
          const productData = localStorage.getItem('analytics_intel_product');
          if (productData) lastProduct = JSON.parse(productData);
          
          const searchData = localStorage.getItem('analytics_intel_search');
          if (searchData) lastSearch = JSON.parse(searchData);
          
          const copyData = localStorage.getItem('analytics_intel_copy');
          if (copyData) lastCopy = JSON.parse(copyData);
        } catch (e) {
          // Parse hatası - varsayılanları kullan
        }
        
        // Son 60 saniye içindeki aktiviteleri kontrol et
        const recentProduct = lastProduct && (now - lastProduct.timestamp < 60000) ? lastProduct : null;
        const recentSearch = lastSearch && (now - lastSearch.timestamp < 30000) ? lastSearch : null;
        const recentCopy = lastCopy && (now - lastCopy.timestamp < 30000) ? lastCopy : null;
        
        // Risk skoru hesapla (rakip karşılaştırma olasılığı)
        let riskScore = 0;
        let riskFactors = [];
        
        if (recentProduct) {
          riskScore += 30;
          riskFactors.push('product_view');
        }
        if (recentSearch) {
          riskScore += 25;
          riskFactors.push('recent_search');
        }
        if (recentCopy) {
          riskScore += 40;
          riskFactors.push('copy_action');
          if (recentCopy.content_type === 'sku' || recentCopy.content_type === 'oem') {
            riskScore += 15;
            riskFactors.push('copy_sku_oem');
          }
        }
        
        // Tab hidden event'i (zenginleştirilmiş)
        queueEvent('tab_hidden', {
          current_page: location.pathname,
          // İstihbarat verileri
          last_product_sku: recentProduct?.sku || null,
          last_product_name: recentProduct?.name || null,
          last_product_price: recentProduct?.price || null,
          last_search_query: recentSearch?.query || null,
          last_search_results: recentSearch?.result_count || null,
          last_copy_content: recentCopy?.content || null,
          last_copy_type: recentCopy?.content_type || null,
          // Risk analizi
          competitor_risk_score: riskScore,
          risk_factors: riskFactors,
          time_on_page_ms: now - pageEnterTimeRef.current
        });
        
        // Heartbeat'i hemen gönder
        sendHeartbeat();
        
        // Eventleri flush et
        flushEvents();
        
        log('Tab gizlendi | Risk:', riskScore, '| Faktörler:', riskFactors.join(', '));
      } else {
        // Tab tekrar görünür - Geri döndü!
        const awayDuration = tabHiddenTimeRef.current ? now - tabHiddenTimeRef.current : 0;
        const awaySeconds = Math.round(awayDuration / 1000);
        
        // Geri dönüş analizi
        let returnAnalysis = 'quick_return';
        if (awaySeconds > 120) {
          returnAnalysis = 'long_absence'; // 2+ dakika - muhtemelen detaylı karşılaştırma yaptı
        } else if (awaySeconds > 30) {
          returnAnalysis = 'comparison_likely'; // 30sn-2dk - hızlı karşılaştırma
        } else if (awaySeconds > 5) {
          returnAnalysis = 'brief_check'; // 5-30sn - kısa kontrol
        }
        
        queueEvent('tab_visible', {
          current_page: location.pathname,
          away_duration_ms: awayDuration,
          away_duration_seconds: awaySeconds,
          return_analysis: returnAnalysis,
          // Geri döndüğünde hala aynı sayfada mı?
          same_page: previousPageRef.current === location.pathname
        });
        
        tabHiddenTimeRef.current = null;
        
        log('Tab görünür | Uzakta:', awaySeconds, 'sn | Analiz:', returnAnalysis);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname, queueEvent, sendHeartbeat, flushEvents, log]);
  
  // ==================== SAYFA KAPATMA ====================
  
  useEffect(() => {
    const handleBeforeUnload = () => {
      // sendBeacon ile güvenilir gönderim
      if (sessionIdRef.current) {
        // Kalan eventleri gönder
        if (eventQueueRef.current.length > 0) {
          navigator.sendBeacon(
            `${ANALYTICS_API}/events/batch`,
            JSON.stringify({
              session_id: sessionIdRef.current,
              events: eventQueueRef.current
            })
          );
        }
        
        // Session'ı bitir
        navigator.sendBeacon(
          `${ANALYTICS_API}/session/end`,
          JSON.stringify({
            session_id: sessionIdRef.current,
            reason: 'tab_close'
          })
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
  
  // ==================== KULLANICI ETKİLEŞİMLERİ ====================
  
  useEffect(() => {
    // Mouse/Keyboard aktivite
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Click tracking
    const handleClick = (e) => {
      handleActivity();
      
      const target = e.target.closest('[data-testid], [data-track], button, a');
      if (!target) return;
      
      const trackData = {
        element_type: target.tagName.toLowerCase(),
        element_id: target.id || null,
        element_class: target.className || null,
        test_id: target.dataset?.testid || null,
        track_id: target.dataset?.track || null,
        text: target.innerText?.substring(0, 50) || null,
        href: target.href || null
      };
      
      queueEvent('click', trackData);
    };
    
    document.addEventListener('click', handleClick);
    
    // Scroll depth tracking
    let maxScrollDepth = 0;
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      
      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;
        
        // Her %25'te bir event
        if (maxScrollDepth % 25 === 0) {
          queueEvent('scroll_depth', {
            depth_percent: maxScrollDepth,
            page_url: location.pathname
          });
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // 🆕 Faz 4: Clipboard tracking (copy/paste)
    const handleCopy = (e) => {
      const selectedText = window.getSelection()?.toString()?.trim();
      if (!selectedText || selectedText.length < 3 || selectedText.length > 100) return;
      
      // İçerik tipini tahmin et
      let contentType = 'text';
      if (/^[A-Z0-9\-]{4,20}$/i.test(selectedText)) {
        contentType = 'sku';
      } else if (/^[A-Z0-9\s\-\.]{6,30}$/i.test(selectedText)) {
        contentType = 'oem';
      } else if (/^\d+([.,]\d{1,2})?$/.test(selectedText)) {
        contentType = 'price';
      }
      
      queueEvent('copy', {
        content: selectedText.substring(0, 100),
        content_type: contentType,
        source_page: location.pathname
      });
    };
    
    const handlePaste = (e) => {
      // Sadece input ve textarea'larda
      if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      
      const pastedText = e.clipboardData?.getData('text')?.trim();
      if (!pastedText || pastedText.length < 3 || pastedText.length > 100) return;
      
      // İçerik tipini tahmin et
      let contentType = 'text';
      if (/^[A-Z0-9\-]{4,20}$/i.test(pastedText)) {
        contentType = 'sku';
      } else if (/^[A-Z0-9\s\-\.]{6,30}$/i.test(pastedText)) {
        contentType = 'oem';
      } else if (/^\d+([.,]\d{1,2})?$/.test(pastedText)) {
        contentType = 'price';
      }
      
      queueEvent('paste', {
        content: pastedText.substring(0, 100),
        content_type: contentType,
        target_field: e.target.name || e.target.id || e.target.placeholder || 'unknown',
        source_page: location.pathname
      });
    };
    
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    
    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handleActivity, queueEvent, location.pathname]);
  
  // ==================== INTERVAL'LAR ====================
  
  useEffect(() => {
    if (!user || !token) return;
    
    // Session başlat
    startSession();
    
    // Heartbeat interval
    heartbeatIntervalRef.current = setInterval(() => {
      checkIdle();
      sendHeartbeat();
    }, CONFIG.HEARTBEAT_INTERVAL);
    
    // Event batch interval
    batchIntervalRef.current = setInterval(() => {
      flushEvents();
    }, CONFIG.EVENT_BATCH_INTERVAL);
    
    // Cleanup - sadece interval'ları temizle, session'ı bitirme
    // Session sadece beforeunload veya gerçek logout'ta biter
    return () => {
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (batchIntervalRef.current) clearInterval(batchIntervalRef.current);
      // NOT: endSession burada çağrılmıyor - beforeunload handler'ı hallediyor
    };
  }, [user?.id, token]); // Sadece user.id ve token değişince yeniden çalış
  
  // Bu komponent görünür UI render etmez
  return null;
};

// Hook export - diğer komponentlerden event göndermek için
export const useActivityTracker = () => {
  const sessionId = localStorage.getItem('analytics_session_id');
  const { token } = useAuth();
  const location = useLocation();
  
  const trackEvent = useCallback(async (eventType, data = {}) => {
    if (!sessionId) return;
    
    try {
      await fetch(`${ANALYTICS_API}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          session_id: sessionId,
          event_type: eventType,
          timestamp: new Date().toISOString(),
          page_url: location.pathname,
          page_type: getPageType(location.pathname),
          data
        })
      });
    } catch (err) {
      console.log('[ActivityTracker] Event error:', err.message);
    }
  }, [sessionId, token, location.pathname]);
  
  // 🆕 Faz 5: İstihbarat context'ini güncelle (localStorage üzerinden)
  const updateIntelligenceContext = useCallback((type, data) => {
    try {
      const contextKey = `analytics_intel_${type}`;
      localStorage.setItem(contextKey, JSON.stringify({
        ...data,
        timestamp: Date.now()
      }));
    } catch (e) {
      // localStorage hatası - sessizce geç
    }
  }, []);
  
  // Özel event fonksiyonları
  const trackProductView = useCallback((sku, productName, price) => {
    trackEvent('product_view', { sku, product_name: productName, price });
    // 🆕 Faz 5: İstihbarat context'ini güncelle
    updateIntelligenceContext('product', { sku, name: productName, price });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.trackProduct(sku, productName, price);
    }
  }, [trackEvent, updateIntelligenceContext]);
  
  const trackSearch = useCallback((query, resultCount, results = []) => {
    trackEvent('search', { 
      query, 
      result_count: resultCount, 
      has_results: resultCount > 0,
      top_results: results.slice(0, 5).map(r => r.sku || r)
    });
    // 🆕 Faz 5: İstihbarat context'ini güncelle
    updateIntelligenceContext('search', { query, result_count: resultCount });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.trackSearch(query, resultCount, results);
    }
  }, [trackEvent, updateIntelligenceContext]);
  
  const trackCartAdd = useCallback((sku, productName, quantity, price) => {
    trackEvent('cart_add', { sku, product_name: productName, quantity, price, total: quantity * price });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.trackCartAdd(sku, productName, quantity, price);
    }
  }, [trackEvent]);
  
  const trackCartRemove = useCallback((sku, productName, quantity) => {
    trackEvent('cart_remove', { sku, product_name: productName, quantity });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.trackCartRemove(sku, quantity);
    }
  }, [trackEvent]);
  
  const trackPurchase = useCallback((orderId, orderValue, items) => {
    trackEvent('purchase', { 
      order_id: orderId, 
      order_value: orderValue, 
      item_count: items?.length || 0 
    });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.trackPurchase(orderId, orderValue, items);
    }
  }, [trackEvent]);
  
  const trackCopy = useCallback((content, contentType = 'text') => {
    trackEvent('copy', { content: content.substring(0, 100), content_type: contentType });
    // 🆕 Faz 5: İstihbarat context'ini güncelle
    updateIntelligenceContext('copy', { content: content.substring(0, 100), content_type: contentType });
    // 🎯 CIP Entegrasyonu
    if (window.YetsanCIP) {
      window.YetsanCIP.track('copy', { content: content.substring(0, 100), content_type: contentType });
    }
  }, [trackEvent, updateIntelligenceContext]);
  
  const trackPaste = useCallback((content, contentType = 'text') => {
    trackEvent('paste', { content: content.substring(0, 100), content_type: contentType });
  }, [trackEvent]);
  
  return {
    trackEvent,
    trackProductView,
    trackSearch,
    trackCartAdd,
    trackCartRemove,
    trackPurchase,
    trackCopy,
    trackPaste,
    sessionId
  };
};

export default ActivityTracker;
